import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Rating,
  Paper,
  Tabs,
  Tab,
  LinearProgress,
  Stepper,
  Step,
  StepButton
} from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const BEHAVIORAL_COMPETENCIES = [
  'Communication (Ability to convey thoughts and feeling with clarity)',
  'Knowledge (Job knowledge, Skills, Competencies & Application)',
  'Quality of Work (Accuracy, Precision, Reliability, less need to re-work)',
  'On-Time Delivery (Delivery as estimated)',
  'Efficiency of Work (turn around time, ability to manage multiple tasks)',
  'Ability to follow process (standards/directions)',
  'Ownership/Accountability',
  'Problem Solving (fire fighting, handling situations, raising escalations timely)',
  'Approach to Work (Initiative, Planning, Following Instructions)',
  'Interpersonal Skills (Interaction with co-workers, superiors, contribution and commitment to team success)'
];

const OPEN_ENDED_QUESTIONS = [
  'Feel free to Share your views if you need any support from the company or any cross functional teams.',
  'What are the key goals and responsibilities you perceive as most significant for you in the upcoming year?',
  "What sort of training/experiences would benefit you in the next year? Not just job-skills - also your natural strengths and personal passions you'd like to develop - you and your work can benefit from these. CHOOSE THOSE AREAS WHERE YOU FEEL YOU NEED TO IMPROVE"
];

const EmployeePerformanceForm = () => {
  const { getAuthHeaders, user } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [ratings, setRatings] = useState({});
  const [kraComments, setKraComments] = useState({});
  const [overallComment, setOverallComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [behavioralRatings, setBehavioralRatings] = useState({});
  const [openEndedAnswers, setOpenEndedAnswers] = useState({});

  // Meeting response states
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [alternateDate, setAlternateDate] = useState('');
  const [alternateTime, setAlternateTime] = useState('');
  const [respondingToMeeting, setRespondingToMeeting] = useState(false);
  const [activeKraTab, setActiveKraTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  const WIZARD_STEPS = ['Overview', 'Performance', 'Competencies', 'Reflection', 'Summary'];

  useEffect(() => {
    fetchMyForms();
  }, []);

  const fetchMyForms = async () => {
    try {
      const response = await axios.get(`${API_URL}/performance/my-forms`, {
        headers: getAuthHeaders()
      });
      setForms(response.data.forms);
    } catch (error) {
      console.error('Error fetching forms:', error);
      toast.error('Failed to fetch performance forms');
    } finally {
      setLoading(false);
    }
  };

  const handleViewForm = async (reviewId) => {
    try {
      const response = await axios.get(`${API_URL}/performance/reviews/${reviewId}`, {
        headers: getAuthHeaders()
      });
      const review = response.data.review;
      console.log('📋 Employee Review Data:', {
        status: review.status,
        appraisalMeeting: review.appraisalMeeting,
        employeeResponseStatus: review.appraisalMeeting?.employeeResponse?.status
      });
      setSelectedReview(review);

      // Initialize ratings from existing employee ratings or create new structure
      const initialRatings = {};
      const initialKraComments = {};
      if (review.form && review.form.kras) {
        review.form.kras.forEach((kra, kraIndex) => {
          // Get KRA comment from first KPI's comment (if exists)
          if (kra.kpis.length > 0) {
            const firstKpiIndex = 0;
            const firstKpiRating = review.employeeRatings?.find(
              r => r.kraIndex === kraIndex && r.kpiIndex === firstKpiIndex
            );
            if (firstKpiRating && firstKpiRating.comment) {
              initialKraComments[kraIndex] = firstKpiRating.comment;
            }
          }
          
          kra.kpis.forEach((kpi, kpiIndex) => {
            const key = `${kraIndex}-${kpiIndex}`;
            const existingRating = review.employeeRatings?.find(
              r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
            );
            initialRatings[key] = existingRating ? existingRating.rating : null;
          });
        });
      }
      setRatings(initialRatings);
      setKraComments(initialKraComments);
      setOverallComment(review.employeeOverallComment || '');
      setActiveKraTab(0);

      // Land on Summary step if the employee needs to respond to a meeting
      const needsMeetingAction =
        review.status === 'meeting_scheduled' &&
        review.appraisalMeeting?.employeeResponse?.status === 'pending';
      setActiveStep(needsMeetingAction ? WIZARD_STEPS.length - 1 : 0);

      // Initialize behavioral ratings
      const initialBehavioral = {};
      if (review.employeeBehavioralRatings) {
        review.employeeBehavioralRatings.forEach(br => {
          initialBehavioral[br.competencyIndex] = br.rating;
        });
      }
      setBehavioralRatings(initialBehavioral);

      // Initialize open-ended answers
      const initialOpenEnded = {};
      if (review.employeeOpenEndedAnswers) {
        review.employeeOpenEndedAnswers.forEach(oea => {
          initialOpenEnded[oea.questionIndex] = oea.answer;
        });
      }
      setOpenEndedAnswers(initialOpenEnded);
    } catch (error) {
      console.error('Error fetching review:', error);
      toast.error('Failed to fetch performance review');
    }
  };

  const handleRatingChange = (kraIndex, kpiIndex, value) => {
    const key = `${kraIndex}-${kpiIndex}`;
    setRatings({ ...ratings, [key]: value });
  };

  const handleSubmit = async () => {
    if (!selectedReview) return;

    // Validate all ratings are provided
    const form = selectedReview.form;
    const missingRatings = [];
    
    form.kras.forEach((kra, kraIndex) => {
      kra.kpis.forEach((kpi, kpiIndex) => {
        const key = `${kraIndex}-${kpiIndex}`;
        if (!ratings[key] || ratings[key] < 1 || ratings[key] > 5) {
          missingRatings.push(`${kra.name} - ${kpi.name}`);
        }
      });
    });

    if (missingRatings.length > 0) {
      toast.error('Please provide ratings for all KPIs');
      return;
    }

    // Validate all 10 behavioural competency ratings
    for (let i = 0; i < 10; i++) {
      if (!behavioralRatings[i] || behavioralRatings[i] < 1) {
        toast.error('Please provide ratings for all Behavioural Competencies');
        return;
      }
    }

    // Validate all 3 open-ended answers
    for (let i = 0; i < 3; i++) {
      if (!openEndedAnswers[i] || !openEndedAnswers[i].trim()) {
        toast.error('Please answer all open-ended questions');
        return;
      }
    }

    // Convert ratings to the required format
    const ratingArray = [];
    form.kras.forEach((kra, kraIndex) => {
      const kraComment = kraComments[kraIndex] || '';
      kra.kpis.forEach((kpi, kpiIndex) => {
        const key = `${kraIndex}-${kpiIndex}`;
        // Store KRA comment in the first KPI's comment field
        ratingArray.push({
          kraIndex,
          kpiIndex,
          rating: ratings[key],
          comment: kpiIndex === 0 ? kraComment : ''
        });
      });
    });

    const behavioralArray = Object.entries(behavioralRatings).map(([idx, rating]) => ({
      competencyIndex: parseInt(idx),
      rating
    }));

    const openEndedArray = Object.entries(openEndedAnswers).map(([idx, answer]) => ({
      questionIndex: parseInt(idx),
      answer
    }));

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/employee-rating`,
        {
          ratings: ratingArray,
          overallComment,
          behavioralRatings: behavioralArray,
          openEndedAnswers: openEndedArray
        },
        {
          headers: getAuthHeaders()
        }
      );
      toast.success('Self-assessment submitted successfully');
      setSelectedReview(null);
      fetchMyForms();
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error(error.response?.data?.message || 'Failed to submit self-assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptMeeting = async () => {
    setRespondingToMeeting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/respond-meeting`,
        { response: 'accepted' },
        { headers: getAuthHeaders() }
      );
      toast.success('Meeting accepted successfully');
      fetchMyForms();
      const updatedReview = await axios.get(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedReview(updatedReview.data.review);
    } catch (error) {
      console.error('Error accepting meeting:', error);
      toast.error(error.response?.data?.message || 'Failed to accept meeting');
    } finally {
      setRespondingToMeeting(false);
    }
  };

  const handleDeclineMeeting = async () => {
    if (!declineReason) {
      toast.error('Please provide a reason for declining');
      return;
    }

    setRespondingToMeeting(true);
    try {
      await axios.post(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}/respond-meeting`,
        {
          response: 'declined',
          declineReason,
          alternateDate: alternateDate || undefined,
          alternateTime: alternateTime || undefined
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Response submitted. Manager will be notified.');
      setShowDeclineForm(false);
      setDeclineReason('');
      setAlternateDate('');
      setAlternateTime('');
      fetchMyForms();
      const updatedReview = await axios.get(
        `${API_URL}/performance/reviews/${selectedReview.reviewId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedReview(updatedReview.data.review);
    } catch (error) {
      console.error('Error declining meeting:', error);
      toast.error(error.response?.data?.message || 'Failed to submit response');
    } finally {
      setRespondingToMeeting(false);
    }
  };

  const getStatusLabel = (form) => {
    switch (form.status) {
      case 'completed':
        return 'Completed';
      case 'meeting_conducted':
        return 'Meeting Conducted';
      case 'meeting_accepted':
        return 'Meeting Accepted';
      case 'meeting_scheduled':
        return 'Meeting Scheduled - Action Required';
      case 'meeting_scheduling_pending':
        return 'Waiting for Manager';
      case 'reschedule_required':
        return 'Waiting for Reschedule';
      case 'manager_submitted':
        return 'Manager Reviewed';
      case 'employee_submitted':
        return 'Submitted';
      default:
        return 'Pending';
    }
  };

  const getStatusColor = (form) => {
    switch (form.status) {
      case 'completed':
      case 'meeting_conducted':
        return 'success';
      case 'meeting_accepted':
        return 'info';
      case 'meeting_scheduled':
        return 'warning';
      case 'meeting_scheduling_pending':
      case 'reschedule_required':
        return 'info';
      case 'manager_submitted':
        return 'info';
      case 'employee_submitted':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (selectedReview) {
    const form = selectedReview.form;
    const isSubmitted = selectedReview.employeeSubmitted;
    const isCompleted = selectedReview.status === 'completed';

    // Calculate length of service
    const calculateLengthOfService = (dateOfJoining) => {
      if (!dateOfJoining) return 'N/A';
      const joinDate = new Date(dateOfJoining);
      const today = new Date();
      const years = today.getFullYear() - joinDate.getFullYear();
      const months = today.getMonth() - joinDate.getMonth();
      const days = today.getDate() - joinDate.getDate();
      
      let totalMonths = years * 12 + months;
      if (days < 0) totalMonths -= 1;
      
      const finalYears = Math.floor(totalMonths / 12);
      const finalMonths = totalMonths % 12;
      
      if (finalYears > 0 && finalMonths > 0) {
        return `${finalYears} Year${finalYears > 1 ? 's' : ''} ${finalMonths} Month${finalMonths > 1 ? 's' : ''}`;
      } else if (finalYears > 0) {
        return `${finalYears} Year${finalYears > 1 ? 's' : ''}`;
      } else if (finalMonths > 0) {
        return `${finalMonths} Month${finalMonths > 1 ? 's' : ''}`;
      } else {
        return 'Less than a month';
      }
    };

    const employee = selectedReview.employeeId || selectedReview.employee;
    // Prioritize dateOfJoining from PersonalInformation, fallback to createdAt
    const dateOfJoining = employee?.dateOfJoining || employee?.createdAt;
    const lengthOfService = calculateLengthOfService(dateOfJoining);

    // Per-step completion (used for stepper check marks while filling)
    const totalKpisCount = form.kras.reduce((s, k) => s + k.kpis.length, 0);
    const ratedKpisCount = Object.values(ratings).filter(Boolean).length;
    const behavioralDone = BEHAVIORAL_COMPETENCIES.every((_, i) => behavioralRatings[i]);
    const openEndedDone = OPEN_ENDED_QUESTIONS.every((_, i) => openEndedAnswers[i] && openEndedAnswers[i].trim());
    const stepComplete = [
      true,
      totalKpisCount > 0 && ratedKpisCount === totalKpisCount,
      behavioralDone,
      openEndedDone,
      !!overallComment
    ];
    const goNext = () => setActiveStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
    const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));
    const isLastStep = activeStep === WIZARD_STEPS.length - 1;

    return (
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">{form.title}</Typography>
          <Button
            variant="outlined"
            onClick={() => setSelectedReview(null)}
          >
            Back to List
          </Button>
        </Box>

        {/* Wizard stepper — one section at a time */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Stepper activeStep={activeStep} alternativeLabel nonLinear>
              {WIZARD_STEPS.map((label, i) => (
                <Step key={label} completed={!isSubmitted && stepComplete[i]}>
                  <StepButton color="inherit" onClick={() => setActiveStep(i)}>
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* STEP 0 — Overview */}
        {activeStep === 0 && (
        <>
        {/* Employee Details Card */}
        <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Employee Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Name</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {employee?.firstName || ''} {employee?.lastName || ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Designation</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {employee?.jobTitle || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Department</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {employee?.department || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Appraisal Year</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {form.reviewYear}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Date of Joining</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {dateOfJoining ? new Date(dateOfJoining).toLocaleDateString('en-GB', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric' 
                  }) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Length of Service</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {lengthOfService}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Form Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Review Cycle</Typography>
                <Typography variant="body1">{form.reviewCycle} {form.reviewYear}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={isCompleted ? 'Completed' : isSubmitted ? 'Submitted' : 'Pending'}
                  color={isCompleted ? 'success' : isSubmitted ? 'info' : 'warning'}
                />
              </Grid>
              {form.description && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Description</Typography>
                  <Typography variant="body1">{form.description}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {isCompleted && selectedReview.calculatedScores && (
          <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Final Performance Score: {selectedReview.calculatedScores.finalScore.toFixed(2)}/5.00
              </Typography>
            </CardContent>
          </Card>
        )}
        </>
        )}

        {/* STEP 1 — Performance Review */}
        {activeStep === 1 && (
        <>
        {/* Performance Review — Card-based section navigation */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Performance Review Details
            </Typography>

            {/* Rating progress bar */}
            {!isSubmitted && (() => {
              const totalKpis = form.kras.reduce((sum, kra) => sum + kra.kpis.length, 0);
              const ratedKpis = Object.values(ratings).filter(Boolean).length;
              return (
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" color="textSecondary">Rating Progress</Typography>
                    <Typography variant="caption" color="textSecondary">{ratedKpis} / {totalKpis} KPIs rated</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={(ratedKpis / totalKpis) * 100} sx={{ borderRadius: 1 }} />
                </Box>
              );
            })()}

            {/* KRA tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={activeKraTab}
                onChange={(e, v) => setActiveKraTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                textColor="primary"
                indicatorColor="primary"
              >
                {form.kras.map((kra, i) => {
                  const allRated = kra.kpis.every((_, j) => ratings[`${i}-${j}`]);
                  return (
                    <Tab
                      key={i}
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <span>{i + 1}. {kra.name.length > 18 ? kra.name.substring(0, 18) + '…' : kra.name}</span>
                          {!isSubmitted && allRated && <span style={{ color: '#2e7d32', fontSize: 13, fontWeight: 700 }}>✓</span>}
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
            </Box>

            {/* Active KRA section */}
            {form.kras.map((kra, kraIndex) =>
              activeKraTab !== kraIndex ? null : (
                <Box key={kraIndex}>
                  {/* Compact KRA header */}
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '50%',
                      bgcolor: 'primary.main', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, flexShrink: 0
                    }}>
                      {kraIndex + 1}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">{kra.name}</Typography>
                      {kra.description && (
                        <Typography variant="caption" color="textSecondary">{kra.description}</Typography>
                      )}
                    </Box>
                  </Box>

                  {/* KPI cards — 2-column compact grid */}
                  <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                    {kra.kpis.map((kpi, kpiIndex) => {
                      const key = `${kraIndex}-${kpiIndex}`;
                      const currentRating = ratings[key];
                      const existingRating = selectedReview.employeeRatings?.find(
                        r => r.kraIndex === kraIndex && r.kpiIndex === kpiIndex
                      );
                      return (
                        <Grid item xs={12} sm={6} key={kpiIndex}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              borderLeft: '3px solid',
                              borderLeftColor: (!isSubmitted && currentRating) ? 'success.main' : 'primary.light',
                              bgcolor: (!isSubmitted && currentRating) ? 'success.50' : 'background.paper',
                              transition: 'all 0.2s',
                              height: '100%'
                            }}
                          >
                            <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                              <Box sx={{
                                width: 20, height: 20, borderRadius: '50%',
                                bgcolor: 'grey.200', color: 'text.secondary',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, fontWeight: 700, flexShrink: 0, mt: 0.2
                              }}>
                                {kpiIndex + 1}
                              </Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.4 }}>
                                {kpi.name}
                              </Typography>
                            </Box>
                            {!isSubmitted ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Rating
                                  value={currentRating || 0}
                                  onChange={(event, newValue) => handleRatingChange(kraIndex, kpiIndex, newValue)}
                                  max={5}
                                  size="small"
                                />
                                <Typography variant="caption" color={currentRating ? 'success.main' : 'text.disabled'}>
                                  {currentRating ? `${currentRating}/5` : '—'}
                                </Typography>
                              </Box>
                            ) : (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Rating value={existingRating?.rating || 0} readOnly max={5} size="small" />
                                <Typography variant="caption">{existingRating?.rating ?? 'N/A'}/5</Typography>
                              </Box>
                            )}
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* KRA comment — compact */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="textSecondary" fontWeight="medium" sx={{ mb: 0.5, display: 'block' }}>
                      Section Comment
                    </Typography>
                    {!isSubmitted ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        value={kraComments[kraIndex] || ''}
                        onChange={(e) => setKraComments({ ...kraComments, [kraIndex]: e.target.value })}
                        placeholder={`Optional comments for ${kra.name}...`}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: kraComments[kraIndex] ? 'normal' : 'italic' }}>
                        {kraComments[kraIndex] || 'No comment provided'}
                      </Typography>
                    )}
                  </Box>

                  {/* Prev / Next */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button size="small" variant="text" onClick={() => setActiveKraTab(activeKraTab - 1)} disabled={activeKraTab === 0}>
                      ← Prev
                    </Button>
                    <Typography variant="caption" color="textSecondary">
                      {activeKraTab + 1} / {form.kras.length}
                    </Typography>
                    <Button size="small" variant="text" onClick={() => setActiveKraTab(activeKraTab + 1)} disabled={activeKraTab === form.kras.length - 1}>
                      Next →
                    </Button>
                  </Box>
                </Box>
              )
            )}
          </CardContent>
        </Card>
        </>
        )}

        {/* STEP 2 — Behavioural Competencies */}
        {activeStep === 2 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Behavioural Competencies</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Rate each competency on a scale of 1–5. This section is not included in the final performance score.
            </Typography>
            <Grid container spacing={1.5}>
              {BEHAVIORAL_COMPETENCIES.map((competency, index) => {
                const currentRating = behavioralRatings[index];
                const existingRating = selectedReview.employeeBehavioralRatings?.find(br => br.competencyIndex === index);
                return (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderLeft: '3px solid',
                        borderLeftColor: (!isSubmitted && currentRating) ? 'success.main' : 'secondary.light',
                        bgcolor: (!isSubmitted && currentRating) ? 'success.50' : 'background.paper',
                        transition: 'all 0.2s',
                        height: '100%'
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                        <Box sx={{
                          width: 20, height: 20, borderRadius: '50%',
                          bgcolor: 'grey.200', color: 'text.secondary',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 700, flexShrink: 0, mt: 0.2
                        }}>
                          {index + 1}
                        </Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.4 }}>
                          {competency}
                        </Typography>
                      </Box>
                      {!isSubmitted ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Rating
                            value={currentRating || 0}
                            onChange={(_, v) => setBehavioralRatings({ ...behavioralRatings, [index]: v })}
                            max={5}
                            size="small"
                          />
                          <Typography variant="caption" color={currentRating ? 'success.main' : 'text.disabled'}>
                            {currentRating ? `${currentRating}/5` : '—'}
                          </Typography>
                        </Box>
                      ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Rating value={existingRating?.rating || 0} readOnly max={5} size="small" />
                          <Typography variant="caption">{existingRating?.rating ?? 'N/A'}/5</Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
        )}

        {/* STEP 3 — Open-Ended Questions */}
        {activeStep === 3 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Open-Ended Questions</Typography>
            {OPEN_ENDED_QUESTIONS.map((question, index) => (
              <Box key={index} sx={{ mb: index < 2 ? 3 : 0 }}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Q{index + 1}. {question}
                </Typography>
                {!isSubmitted ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={openEndedAnswers[index] || ''}
                    onChange={(e) => setOpenEndedAnswers({ ...openEndedAnswers, [index]: e.target.value })}
                    placeholder="Your answer..."
                    required
                    size="small"
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: openEndedAnswers[index] ? 'normal' : 'italic', pl: 1 }}>
                    {openEndedAnswers[index] || 'No answer provided'}
                  </Typography>
                )}
                {index < 2 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </CardContent>
        </Card>
        )}

        {/* STEP 4 — Summary, comments, meeting & submission */}
        {activeStep === 4 && (
        <>
        {!isSubmitted && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Overall Comments</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={overallComment}
                onChange={(e) => setOverallComment(e.target.value)}
                placeholder="Add any additional comments about your performance..."
              />
            </CardContent>
          </Card>
        )}

        {isCompleted && selectedReview.employeeOverallComment && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Your Comments</Typography>
              <Typography variant="body1">{selectedReview.employeeOverallComment}</Typography>
            </CardContent>
          </Card>
        )}

        {/* Manager comments are not visible to employees */}

        {/* Meeting Invitation - Pending Response */}
        {selectedReview.status === 'meeting_scheduled' && selectedReview.appraisalMeeting?.employeeResponse?.status === 'pending' && (
          <Card sx={{  mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'warning.dark' }}>
                📅 Appraisal Meeting Scheduled - Action Required
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Your manager has scheduled an appraisal meeting to discuss your performance review.
              </Alert>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Meeting Date</Typography>
                  <Typography variant="h6">
                    {new Date(selectedReview.appraisalMeeting.proposedDate).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Meeting Time</Typography>
                  <Typography variant="h6">{selectedReview.appraisalMeeting.proposedTime}</Typography>
                </Grid>
              </Grid>

              {!showDeclineForm ? (
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => setShowDeclineForm(true)}
                    disabled={respondingToMeeting}
                  >
                    Decline & Suggest Alternate
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAcceptMeeting}
                    disabled={respondingToMeeting}
                    size="large"
                  >
                    {respondingToMeeting ? <CircularProgress size={24} /> : 'Accept Meeting'}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" gutterBottom color="error">
                    Decline Meeting
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason for Declining *"
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        placeholder="Please explain why you cannot attend at the proposed time..."
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Suggested Alternate Date"
                        type="date"
                        value={alternateDate}
                        onChange={(e) => setAlternateDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Suggested Alternate Time"
                        type="time"
                        value={alternateTime}
                        onChange={(e) => setAlternateTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                  <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowDeclineForm(false);
                        setDeclineReason('');
                        setAlternateDate('');
                        setAlternateTime('');
                      }}
                      disabled={respondingToMeeting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleDeclineMeeting}
                      disabled={respondingToMeeting || !declineReason}
                    >
                      {respondingToMeeting ? <CircularProgress size={24} /> : 'Submit Decline'}
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Meeting Accepted */}
        {(selectedReview.status === 'meeting_accepted' || selectedReview.status === 'completed') && 
         selectedReview.appraisalMeeting?.employeeResponse?.status === 'accepted' && (
          <Card sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300'}}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.dark' }}>
                ✅ Meeting Confirmed
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Meeting Date:</strong> {new Date(selectedReview.appraisalMeeting.proposedDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Meeting Time:</strong> {selectedReview.appraisalMeeting.proposedTime}
              </Typography>
              {selectedReview.status === 'meeting_accepted' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Your appraisal meeting is confirmed. Please ensure you're available at the scheduled time.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Completed Appraisal - Show Full Documentation */}
        {isCompleted && selectedReview.appraisalMeeting?.meetingConducted && (
          <Card sx={{ mb: 3,  border: '2px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.dark' }}>
                ✅ Appraisal Process Completed
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your appraisal meeting was conducted on{' '}
                {new Date(selectedReview.appraisalMeeting.conductedAt).toLocaleDateString()}.
                Below is the documentation from your appraisal discussion.
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Manager Commitments
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedReview.appraisalMeeting.managerCommitments}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Your Commitments
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedReview.appraisalMeeting.employeeCommitments}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Final Review Decision
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {selectedReview.appraisalMeeting.finalReviewDecision}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Overall Feedback / Summary
                  </Typography>
                  <Typography variant="body1">
                    {selectedReview.appraisalMeeting.overallFeedback}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
        </>
        )}

        {/* Wizard navigation */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={goBack} disabled={activeStep === 0}>
            ← Back
          </Button>
          <Typography variant="body2" color="textSecondary">
            Step {activeStep + 1} of {WIZARD_STEPS.length} — {WIZARD_STEPS[activeStep]}
          </Typography>
          {!isLastStep ? (
            <Button variant="contained" onClick={goNext}>
              Next →
            </Button>
          ) : !isSubmitted ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} /> : 'Submit Self-Assessment'}
            </Button>
          ) : (
            <Button variant="outlined" onClick={() => setSelectedReview(null)}>
              Back to List
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>My Performance Reviews</Typography>
      
      {forms.length === 0 ? (
        <Alert severity="info">No performance reviews assigned to you.</Alert>
      ) : (
        <Grid container spacing={3}>
          {forms.map((item) => (
            <Grid item xs={12} md={6} key={item.reviewId}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {item.form.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {item.form.reviewCycle} {item.form.reviewYear}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Manager: {item.manager?.firstName} {item.manager?.lastName}
                  </Typography>
                  <Box mt={2}>
                    <Chip
                      label={getStatusLabel(item)}
                      color={getStatusColor(item)}
                      sx={{ mr: 1 }}
                    />
                    {item.status === 'meeting_scheduled' && item.appraisalMeeting?.employeeResponse?.status === 'pending' && (
                      <Chip
                        label="Action Required"
                        color="error"
                        size="small"
                        sx={{ animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }}
                      />
                    )}
                    {item.finalScore && (
                      <Chip
                        label={`Score: ${item.finalScore.toFixed(2)}/5.00`}
                        color="primary"
                      />
                    )}
                  </Box>
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      onClick={() => handleViewForm(item.reviewId)}
                    >
                      {item.employeeSubmitted ? 'View Details' : 'Start Review'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default EmployeePerformanceForm;

